import { Component, DestroyRef, ElementRef, inject, Input, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationStart, Router } from '@angular/router';
import { ToastContainerDirective, ToastrService } from 'ngx-toastr';
import { from, forkJoin, map, of, filter, switchMap, catchError, take, shareReplay, tap } from 'rxjs';
import { AlertsRepositoryServiceService, ConfigurationState, CorporationAlertsRepositoryServiceService, eAlertHubEventType, eAlertStatus, eOrderTypeGroup, MessagesStateService, NavItem, NotificationService, OrderRepositoryService } from 'reg-hub-common';
import { MatSidenavContainer, MatSidenavContent } from '@angular/material/sidenav';
import { EagerFetchRepositoryService } from '../../services/eager-fetch-repository/eager-fetch-repository.service';
import { AlertsHubService } from '../../services/hubs/alerts-hub/alerts-hub.service';

@Component({
  selector: 'app-side-menu',
  templateUrl: './side-menu.component.html',
  styleUrls: ['./side-menu.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class SideMenuComponent {
  readonly navLabelKeys: Record<string, string> = { 'Dashboard': 'navigation.menu.dashboard', 'Order History': 'navigation.menu.orderHistory', 'Hub Report Management': 'navigation.menu.hubReportManagement', 'Messages': 'navigation.menu.messages', 'Bundles': 'navigation.menu.bundles', 'Reporting': 'navigation.menu.reporting', 'VINsight': 'navigation.menu.vinsight', 'Information': 'navigation.menu.information', 'Contact Us': 'navigation.menu.contactUs', 'System Events': 'navigation.menu.systemEvents', 'Alerts': 'navigation.menu.alerts', 'Management': 'navigation.menu.management', 'Accounting': 'navigation.menu.accounting', 'PPSA Registration': 'navigation.menu.ppsaRegistration', 'PPSA Search': 'navigation.menu.ppsaSearch', 'Know Your Business': 'navigation.menu.knowYourBusiness', 'Know Your Customer': 'navigation.menu.knowYourCustomer', 'Land Services': 'navigation.menu.landServices' };
  @Input() isSidenavOpen: boolean = true;

  @ViewChild(ToastContainerDirective, { static: true }) toastContainer!: ToastContainerDirective;
  @ViewChild('content') private content!: MatSidenavContent;

  private destroyRef = inject(DestroyRef);

  selectedItem = '';
  isSearching = false;
  disableRipple = true;
  unreadMessageCount: number = 0;
  activeEventsCount: number = 0;
  newAlertCount: number = 0;

  // This will come from a DB call to which nav items a user is allowed to see
  baseNavItems: NavItem[] = [
    { label: 'Dashboard', icon: 'home', link: '/dashboard' },
    { label: 'Order History', icon: 'list_alt', link: '/orders' },
    { label: 'Hub Report Management', icon: 'graph_5', link: '/hubreportsv2' }, //UBO2TODO: rename path
    { label: 'Messages', icon: 'message', link: '/messages' },
    { label: 'Bundles', icon: 'library_books', link: '/bundles' },
    { label: 'Reporting', icon: 'summarize', link: '/reports' }
  ];
  navItems$ = of<NavItem[]>([]);

  baseRequestItems: NavItem[] = [
    { label: 'VINsight', icon: 'wb_incandescent', link: '/vinsights' }
  ];
  requestItems$ = of<NavItem[]>([]);

  helpItems: NavItem[] = [
    { label: 'Information', icon: 'quiz', link: '/information-documents' },
    { label: 'Contact Us', icon: 'support_agent', link: '/contact-us' },
    { label: 'System Events', icon: 'event_note', link: '/system-events' },
  ];

  constructor(
    private messageService: ToastrService,
    private configurationState: ConfigurationState,
    private router: Router,
    private ordersRepo: OrderRepositoryService,
    private messagesStateService: MessagesStateService,
    private corporationAlertsRepo: CorporationAlertsRepositoryServiceService,
    private alertsRepo: AlertsRepositoryServiceService,
    private alertsHubService: AlertsHubService,
    private notificationService: NotificationService,
    private eagerFetchService: EagerFetchRepositoryService) { }

  ngOnInit() {
    this.ordersRepo.scroll$.subscribe(() => {
      setTimeout(() => {
        this.content.scrollTo({ top: this.content.getElementRef().nativeElement.scrollHeight, behavior: 'smooth' });
      }, 75) //would be nice pass value for scroll to so we don't always scroll all the way to the bottom
    });

    this.messageService.overlayContainer = this.toastContainer;

    this.router.events
      .pipe(filter(event => event instanceof NavigationStart), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.messageService.clear());

    // Subscribe to unread message updates from the service
    this.messagesStateService.unreadMessagesCount$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(count => {
      this.unreadMessageCount = count;
    });
  }

  ngAfterViewInit() {
    this.ordersRepo.getUnreadClientMessageCount().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(count => {
      this.messagesStateService.setUnreadMessageCount(count);
    });

    // Eager fetch only warms BehaviorSubjects for request pages; it doesn't need to block the nav items
    this.eagerFetchService.fetchInitialRequestPageOptions().pipe(
      catchError(() => of(null)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();

    const config$ = from(this.configurationState.setConfiguration()).pipe(
      switchMap(() =>
        forkJoin({
          hasMgmt: from(this.configurationState.hasUserManagementAccess()).pipe(
            take(1),
            catchError(() => of(false))
          ),
          hasFees: from(this.configurationState.hasAccountingAccess()).pipe(
            take(1),
            catchError(() => of(false))
          ),
          // The user's configuration must allow the tab and the corporation must have an alert enabled
          hasAlerts: from(this.configurationState.hasAlertsTabVisibility()).pipe(
            switchMap(hasAlertsTabVisibility => hasAlertsTabVisibility
              ? from(this.configurationState.getCorporationID()).pipe(
                  // Ask only for enabled alerts; an unfiltered first page could miss them once there are more than 10 alert types
                  switchMap(corporationID => this.corporationAlertsRepo.getCorporationAlerts(corporationID, new HttpParams().set('enabled', true).set('pageSize', 1))),
                  map(corporationAlerts => corporationAlerts.length > 0)
                )
              : of(false)),
            take(1),
            catchError(() => of(false))
          ),
          hasPPSARegistrations: from(this.configurationState.canDraftOrderTypeGroup(eOrderTypeGroup.PPSARegistration)).pipe(
            take(1),
            catchError(() => of(false))
          ),
          hasPPSASearches: from(this.configurationState.canDraftOrderTypeGroup(eOrderTypeGroup.PPSASearch)).pipe(
            take(1),
            catchError(() => of(false))
          ),
          hasBusinessSearch: from(this.configurationState.canDraftOrderTypeGroup(eOrderTypeGroup.BusinessSearch)).pipe(
            take(1),
            catchError(() => of(false))
          ),
          hasKnowYourCustomer: from(this.configurationState.canDraftOrderTypeGroup(eOrderTypeGroup.KnowYourCustomer)).pipe(
            take(1),
            catchError(() => of(false))),
          hasLandServices: from(this.configurationState.canDraftOrderTypeGroup(eOrderTypeGroup.LandServices)).pipe(
            take(1),
            catchError(() => of(false))
          )
        })
      ),
      // navItems$ and requestItems$ both subscribe; share so the requests above only run once
      shareReplay(1)
    );

    this.navItems$ = config$.pipe(
      tap(({ hasAlerts }) => {
        if (hasAlerts) this.monitorNewAlerts();
      }),
      map(({ hasMgmt, hasFees, hasAlerts }) => {
        const items = [...this.baseNavItems];
        if (hasAlerts) {
          const messagesIndex = items.findIndex(i => i.link === '/messages');
          items.splice(messagesIndex + 1, 0, { label: 'Alerts', icon: 'notifications', link: '/alerts' });
        }
        if (hasMgmt) items.push({ label: 'Management', icon: 'settings', link: '/management' });
        if (hasFees) items.push({ label: 'Accounting', icon: 'payment', link: '/accounting' });

        return items;
      }),
      shareReplay(1)
    );

    this.requestItems$ = config$.pipe(
      map(({ hasPPSARegistrations, hasPPSASearches, hasBusinessSearch, hasKnowYourCustomer, hasLandServices }) => {
        const requestItems = [...this.baseRequestItems];
        if (hasPPSARegistrations) requestItems.push({ label: 'PPSA Registration', icon: 'draw', link: '/orders/ppsa-registration' });
        if (hasPPSASearches) requestItems.push({ label: 'PPSA Search', icon: 'search', link: '/orders/ppsa-search' });
        if (hasBusinessSearch) requestItems.push({ label: 'Know Your Business', icon: 'corporate_fare', link: '/orders/business' });
        if (hasKnowYourCustomer) requestItems.push({ label: 'Know Your Customer', icon: 'person_search', link: '/orders/kyc' });
        if (hasLandServices) requestItems.push({ label: 'Land Services', icon: 'map_search', link: '/orders/land' });

        return requestItems;
      }),
      shareReplay(1)
    );
  }

  badgeContent(item: NavItem): string {
    switch (item.link) {
      case '/messages':
      case '/alerts':
        return '!';
      default:
        return '';
    }
  }

  isBadgeHidden(item: NavItem): boolean {
    switch (item.link) {
      case '/messages':
        return this.unreadMessageCount < 1;
      case '/alerts':
        return this.newAlertCount < 1;
      default:
        return true;
    }
  }

  // Only users who can see the Alerts tab get here. The initial count is loaded before connecting
  // so live updates are applied on top of it rather than being overwritten by it.
  private monitorNewAlerts() {
    from(this.configurationState.getCorporationID()).pipe(
      switchMap(corporationID => this.alertsRepo.getNewAlertCount(corporationID)),
      take(1),
      catchError(() => of(0)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(count => {
      this.alertsHubService.setInitialNewAlertCount(count);
      this.alertsHubService.connect();
    });

    this.alertsHubService.newAlertCount$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(count => {
      this.newAlertCount = count;
    });

    this.alertsHubService.alertEvents$.pipe(
      filter(alertEvent => alertEvent.eventType === eAlertHubEventType.Created && alertEvent.alert.alertStatusID === eAlertStatus.New),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(alertEvent => this.notificationService.addNotification({ id: 0, alert: alertEvent.alert }));
  }

  handleClick(selectedItem: NavItem) {
    this.selectedItem = selectedItem.label;
  }

  onSearch(searchText: string) {
    this.isSearching = true;

    this.router.navigate(['orders', searchText]);
  }
}
